/**
 * The Sources page's two dialogs: "Propose a new source" (Figma 4641:58988), which sends a public source for
 * CortexGrip's review, and "Add competitor" (4641:59556), which starts watching a competitor's public website.
 * Both check the address as it's typed ("Public website, reachable"); submitting checks it again.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useId, useRef, useState, type FormEvent, type ReactNode } from "react";

import { useOrganization } from "@/auth/organization";
import { Button, cx, Form, Icon, Sheet, useToast, type IconName } from "@/components/ui";
import { portal } from "@/lib/api";
import { growthKey, jurisdictionName, SOURCE_TYPE_LABEL, useStoreProfile } from "@/lib/growth";
import { ApiError, errorMessage } from "@/lib/http";
import type { GrowthNiche, GrowthNicheCatalogue, GrowthProfile, GrowthSourceProposal } from "@/lib/types";

/** The browser's own focus outline is off (`!` beats index.css's unlayered `:focus-visible` rule): focus shows as
 * the field's rounded border and soft ring, never a second box around or inside it. */
const input =
  "w-full rounded-xl border border-border bg-white px-3.5 py-[11px] text-[13.5px] text-fg outline-none! placeholder:text-fg-subtle focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20";
const cancelButton =
  "rounded-full border border-border bg-white px-5 py-[11px] text-sm font-semibold text-fg transition-colors hover:bg-brand-50";
const submitButton =
  "rounded-full bg-brand-500 px-5 py-[11px] text-sm font-semibold text-white transition-colors hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-60";
const problemText = "text-xs text-[#b8291f]";

/** The types a person may call the source they propose (the register's own also has Local authority). */
const PROPOSED_TYPES: { id: NonNullable<GrowthSourceProposal["source_type"]>; label: string }[] = [
  { id: "official", label: SOURCE_TYPE_LABEL.official },
  { id: "sector_body", label: SOURCE_TYPE_LABEL.sector_body },
  { id: "market_signal", label: SOURCE_TYPE_LABEL.market_signal },
  { id: "public_web", label: SOURCE_TYPE_LABEL.public_web },
];

// ── the pieces both dialogs share ───────────────────────────────────────────

type Check =
  | { state: "idle" }
  | { state: "checking"; url: string }
  | { state: "ok"; url: string }
  | { state: "problem"; url: string; message: string };

/** The live address check: run when the field is left; typing again clears its answer. */
function useAddressCheck() {
  const { orgId } = useOrganization();
  const [check, setCheck] = useState<Check>({ state: "idle" });
  const latest = useRef("");
  const run = async (raw: string) => {
    const url = raw.trim();
    if (url === latest.current && check.state !== "idle") return;
    latest.current = url;
    if (!url) return setCheck({ state: "idle" });
    setCheck({ state: "checking", url });
    try {
      await portal.checkUrl(orgId, url);
      if (latest.current === url) setCheck({ state: "ok", url });
    } catch (err) {
      if (latest.current === url) setCheck({ state: "problem", url, message: errorMessage(err) });
    }
  };
  const clear = () => {
    latest.current = "";
    setCheck({ state: "idle" });
  };
  return { check, run, clear };
}

function DialogShell({
  icon,
  title,
  description,
  onClose,
  onSubmit,
  error,
  submit,
  children,
}: {
  icon: IconName;
  title: string;
  description: string;
  onClose: () => void;
  onSubmit: () => void;
  /** What went wrong that isn't one field's ("Your plan's slots are in use"). */
  error: string | null;
  submit: ReactNode;
  children: ReactNode;
}) {
  const titleId = useId();
  const send = (e: FormEvent) => {
    e.preventDefault();
    onSubmit();
  };
  return (
    <Sheet
      onClose={onClose}
      labelledBy={titleId}
      className="max-w-[580px] rounded-[20px] shadow-[0px_16px_48px_0px_rgba(26,15,10,0.28)]"
    >
      <Form onSubmit={send} className="flex flex-col gap-[18px] px-5 pt-6 pb-[22px] sm:px-7">
        <div className="flex items-start gap-3.5">
          <span className="flex size-10 shrink-0 items-center justify-center rounded-xl border border-brand-100 bg-[#fef3f0] text-brand-500">
            <Icon name={icon} size={18} />
          </span>
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <h2 id={titleId} className="text-xl font-bold text-fg">
              {title}
            </h2>
            <p className="text-[13.5px] leading-[1.55] text-fg-muted">{description}</p>
          </div>
        </div>
        {children}
        {error && (
          <p role="alert" className="text-[13px] text-[#b8291f]">
            {error}
          </p>
        )}
        <div className="h-px bg-border" />
        <div className="flex flex-wrap items-center justify-end gap-2.5">
          <Button unstyled type="button" onClick={onClose} className={cancelButton}>
            Cancel
          </Button>
          {submit}
        </div>
      </Form>
    </Sheet>
  );
}

function FieldLabel({ htmlFor, children, hint }: { htmlFor?: string; children: ReactNode; hint?: string }) {
  const Tag = htmlFor ? "label" : "p";
  return (
    <Tag htmlFor={htmlFor} className="flex items-center gap-1.5 leading-normal">
      <span className="text-[13.5px] font-semibold text-fg">{children}</span>
      {hint && <span className="text-xs text-fg-subtle">{hint}</span>}
    </Tag>
  );
}

/** A website field with the live check under it, or the reason the server refused the address. */
function AddressField({
  label,
  value,
  onChange,
  check,
  refused,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  check: ReturnType<typeof useAddressCheck>;
  refused: string | undefined;
  placeholder: string;
}) {
  const id = useId();
  const problem = refused ?? (check.check.state === "problem" ? check.check.message : undefined);
  return (
    <div className="flex flex-col gap-2">
      <FieldLabel htmlFor={id} hint="Required">
        {label}
      </FieldLabel>
      <div
        className={cx(
          "flex items-center gap-2 rounded-xl border bg-white px-3.5 focus-within:border-brand-500 focus-within:ring-2 focus-within:ring-brand-500/20",
          problem ? "border-[#b8291f]" : "border-border",
        )}
      >
        <Icon name="link" size={15} className="text-fg-subtle" />
        <input
          id={id}
          type="text"
          inputMode="url"
          autoComplete="url"
          maxLength={2048}
          value={value}
          placeholder={placeholder}
          aria-invalid={problem ? true : undefined}
          aria-describedby={`${id}-check`}
          onChange={(e) => {
            onChange(e.target.value);
            check.clear();
          }}
          onBlur={() => void check.run(value)}
          className="min-w-0 flex-1 bg-transparent py-[11px] text-[13.5px] text-fg outline-none! placeholder:text-fg-subtle"
        />
      </div>
      <div id={`${id}-check`} aria-live="polite">
        {problem ? (
          <p className={problemText}>{problem}</p>
        ) : check.check.state === "checking" ? (
          <p className="text-xs text-fg-subtle">Checking the address…</p>
        ) : check.check.state === "ok" ? (
          <p className="flex items-center gap-[5px] text-xs text-[#128c61]">
            <Icon name="check-circle" size={13} />
            Public website, reachable
          </p>
        ) : null}
      </div>
    </div>
  );
}

function ToggleChip({ on, onClick, children }: { on: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <Button
      unstyled
      type="button"
      aria-pressed={on}
      onClick={onClick}
      className={cx(
        "flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[12.5px] leading-normal font-semibold transition-colors",
        on
          ? "border-brand-500 bg-[#fef3f0] text-brand-500"
          : "border-border bg-white text-fg-muted hover:bg-brand-50",
      )}
    >
      {on && <Icon name="check" size={12} />}
      {children}
    </Button>
  );
}

const toggle = (list: string[], id: string) =>
  list.includes(id) ? list.filter((x) => x !== id) : [...list, id];
const refusedField = (err: unknown, field: string) =>
  err instanceof ApiError ? err.fieldError(field) : undefined;
/** A refusal that isn't about one of the form's fields, shown above the buttons. */
function formProblem(err: unknown, fields: string[]): string | null {
  if (!err) return null;
  if (err instanceof ApiError && err.fieldErrors.some((e) => fields.includes(e.field))) return null;
  return errorMessage(err);
}

// ── Propose a new source (Figma 4641:58988) ─────────────────────────────────

export function ProposeSourceDialog({
  niche,
  catalogue,
  onClose,
}: {
  niche: GrowthNiche;
  catalogue: GrowthNicheCatalogue | undefined;
  onClose: () => void;
}) {
  const { orgId } = useOrganization();
  const qc = useQueryClient();
  const toast = useToast();
  const check = useAddressCheck();
  const ids = useId();
  const [url, setUrl] = useState("");
  const [name, setName] = useState("");
  const [jurisdiction, setJurisdiction] = useState("");
  const [type, setType] = useState<GrowthSourceProposal["source_type"]>();
  const [topics, setTopics] = useState<string[]>([]);
  const [reason, setReason] = useState("");
  const places = [...new Set([...niche.jurisdictions, "uk", "global"])];

  const propose = useMutation({
    mutationFn: (body: GrowthSourceProposal) => portal.proposeSource(orgId, body),
    onSuccess: (source) => {
      void qc.invalidateQueries({ queryKey: growthKey(orgId, "sources") });
      toast.show({
        tone: "success",
        title: "Sent for review",
        body: `We'll review ${source.name} before it's used. It shows as Proposed until then.`,
      });
      onClose();
    },
  });
  const ready = url.trim() !== "" && name.trim() !== "";
  const submit = () => {
    if (!ready || propose.isPending) return;
    propose.mutate({
      url: url.trim(),
      name: name.trim(),
      jurisdiction: jurisdiction || undefined,
      source_type: type,
      topics,
      reason: reason.trim() || undefined,
    });
  };

  return (
    <DialogShell
      icon="database"
      title="Propose a new source"
      description="Tell us about a public source you'd like us to monitor. Our team reviews it first, and it isn't used until it's approved."
      onClose={onClose}
      onSubmit={submit}
      error={formProblem(propose.error, ["url", "name", "jurisdiction", "topics"])}
      submit={
        <Button
          unstyled
          type="submit"
          loading={propose.isPending}
          disabledReason={ready ? undefined : "Enter the source's address and name."}
          className={submitButton}
        >
          {propose.isPending ? "Sending…" : "Submit for review"}
        </Button>
      }
    >
      <AddressField
        label="Source website"
        value={url}
        onChange={setUrl}
        check={check}
        refused={refusedField(propose.error, "url")}
        placeholder="https://www.example.org/research"
      />

      <div className="flex flex-col gap-[18px] sm:flex-row sm:gap-3">
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <FieldLabel htmlFor={`${ids}-name`} hint="Required">
            Source name
          </FieldLabel>
          <input
            id={`${ids}-name`}
            value={name}
            maxLength={200}
            onChange={(e) => setName(e.target.value)}
            aria-invalid={refusedField(propose.error, "name") ? true : undefined}
            className={input}
          />
          {refusedField(propose.error, "name") && (
            <p className={problemText}>{refusedField(propose.error, "name")}</p>
          )}
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <FieldLabel htmlFor={`${ids}-place`}>Jurisdiction</FieldLabel>
          <div className="relative">
            <select
              id={`${ids}-place`}
              value={jurisdiction}
              onChange={(e) => setJurisdiction(e.target.value)}
              className={cx(input, "h-10 appearance-none py-0 pr-9")}
            >
              <option value="">Choose a place</option>
              {places.map((p) => (
                <option key={p} value={p}>
                  {jurisdictionName(p, catalogue)}
                </option>
              ))}
            </select>
            <Icon
              name="chevron-down"
              size={20}
              className="pointer-events-none absolute top-1/2 right-2.5 -translate-y-1/2 text-fg-subtle"
            />
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <FieldLabel>Source type</FieldLabel>
        <div role="group" aria-label="Source type" className="flex flex-wrap gap-2">
          {PROPOSED_TYPES.map((t) => (
            <ToggleChip
              key={t.id}
              on={type === t.id}
              onClick={() => setType(type === t.id ? undefined : t.id)}
            >
              {t.label}
            </ToggleChip>
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <FieldLabel>Topics it covers</FieldLabel>
        <div role="group" aria-label="Topics it covers" className="flex flex-wrap gap-2">
          {niche.topics.map((t) => (
            <ToggleChip key={t.id} on={topics.includes(t.id)} onClick={() => setTopics(toggle(topics, t.id))}>
              {t.label}
            </ToggleChip>
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <FieldLabel htmlFor={`${ids}-why`}>Why is it useful?</FieldLabel>
        <textarea
          id={`${ids}-why`}
          value={reason}
          maxLength={1000}
          onChange={(e) => setReason(e.target.value)}
          className={cx(input, "h-[72px] resize-y leading-[1.55]")}
        />
      </div>

      <div className="flex items-start gap-2 rounded-[10px] bg-muted px-3 py-2.5 text-[12.5px] leading-[1.55] text-fg-muted">
        <Icon name="info-circle" size={14} className="mt-[3px]" />
        <p>
          Public sources only. We can't monitor login-only pages, social media accounts or anyone's private
          data.
        </p>
      </div>
    </DialogShell>
  );
}

// ── Add competitor (Figma 4641:59556) ───────────────────────────────────────

export function AddCompetitorDialog({
  niche,
  profile,
  onClose,
}: {
  niche: GrowthNiche;
  profile: GrowthProfile;
  onClose: () => void;
}) {
  const { orgId } = useOrganization();
  const qc = useQueryClient();
  const toast = useToast();
  const store = useStoreProfile();
  const check = useAddressCheck();
  const ids = useId();
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [watch, setWatch] = useState<string[]>(niche.watch.map((o) => o.id));
  const [confirmed, setConfirmed] = useState(false);

  const add = useMutation({
    mutationFn: () =>
      portal.addCompetitor(orgId, { name: name.trim(), url: url.trim(), watch, confirmed: true }),
    onSuccess: (competitor) => {
      // shown at once; the profile fetched again brings the worker's progress, and the scan toast follows it
      store({ ...profile, competitors: [...profile.competitors, competitor] });
      void qc.invalidateQueries({ queryKey: ["growth-profile", orgId] });
      toast.show({
        tone: "success",
        title: `Watching ${competitor.name}`,
        body: "We'll read their public website now. Insights appear as soon as anything is found.",
      });
      onClose();
    },
  });
  const ready = name.trim() !== "" && url.trim() !== "" && watch.length > 0 && confirmed;
  const missing =
    name.trim() === "" || url.trim() === ""
      ? "Enter the competitor's name and website."
      : watch.length === 0
        ? "Pick what to watch first."
        : !confirmed
          ? "Confirm the website is public first."
          : undefined;
  const submit = () => {
    if (ready && !add.isPending) add.mutate();
  };

  return (
    <DialogShell
      icon="target"
      title="Add competitor"
      description="We'll watch their public website for changes to campaigns, offers and intake pages. We never collect private data."
      onClose={onClose}
      onSubmit={submit}
      error={formProblem(add.error, ["url", "name", "watch", "confirmed"])}
      submit={
        <Button
          unstyled
          type="submit"
          loading={add.isPending}
          disabledReason={missing}
          className={submitButton}
        >
          {add.isPending ? "Adding…" : "Add competitor"}
        </Button>
      }
    >
      <div className="flex flex-col gap-2">
        <FieldLabel htmlFor={`${ids}-name`} hint="Required">
          Competitor name
        </FieldLabel>
        <input
          id={`${ids}-name`}
          value={name}
          maxLength={200}
          onChange={(e) => setName(e.target.value)}
          className={input}
        />
        {refusedField(add.error, "name") && <p className={problemText}>{refusedField(add.error, "name")}</p>}
      </div>

      <AddressField
        label="Competitor website"
        value={url}
        onChange={setUrl}
        check={check}
        refused={refusedField(add.error, "url")}
        placeholder="https://www.example.co.uk"
      />

      <div className="flex flex-col gap-2">
        <FieldLabel hint="Public pages only">What should we watch?</FieldLabel>
        <div role="group" aria-label="What should we watch?" className="flex flex-wrap gap-2">
          {niche.watch.map((o) => (
            <ToggleChip key={o.id} on={watch.includes(o.id)} onClick={() => setWatch(toggle(watch, o.id))}>
              {o.label}
            </ToggleChip>
          ))}
        </div>
        {watch.length === 0 && <p className={problemText}>Choose at least one thing to watch.</p>}
      </div>

      <label className="flex cursor-pointer items-start gap-2.5 rounded-xl border border-border px-3.5 py-3">
        <input
          type="checkbox"
          checked={confirmed}
          onChange={(e) => setConfirmed(e.target.checked)}
          className="peer sr-only"
        />
        <span
          aria-hidden
          className={cx(
            "mt-0.5 flex size-[18px] shrink-0 items-center justify-center rounded-[5px] border text-[10px] font-bold text-white peer-focus-visible:ring-2 peer-focus-visible:ring-brand-500/40",
            confirmed ? "border-[#12a97e] bg-[#12a97e]" : "border-[#d4cac4] bg-white",
          )}
        >
          {confirmed && "✓"}
        </span>
        <span className="text-[13.5px] leading-[1.55] text-fg">
          I confirm this is the public website of a business we compete with.
        </span>
      </label>
    </DialogShell>
  );
}
